<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $props.wbsCode ? $t('ProjectSettings.ProjectTab.WbsCodeModal.title.update') : $t('ProjectSettings.ProjectTab.WbsCodeModal.title.add') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full overflow-y-auto">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveWbsCode"
        >
          <label>
            {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.code') }}

            <AppInput
              v-model="wbsCodeForm.form.code"
              name="code"
              type="text"
              :error="wbsCodeForm.getErrors('code')"
              data-test="update-code"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.description') }}

            <AppInput
              v-model="wbsCodeForm.form.description"
              name="description"
              type="text"
              :error="wbsCodeForm.getErrors('description')"
              data-test="update-description"
              class="form-input"
            />
          </label>

          <div class="flex space-x-4">
            <label class="w-full">
              {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.unit') }}

              <AppInput
                v-model="wbsCodeForm.form.unit"
                name="unit"
                type="text"
                :error="wbsCodeForm.getErrors('unit')"
                data-test="update-unit"
                class="form-input"
              />
            </label>

            <label
              v-if="!project?.isClient"
              class="w-full"
            >
              <p>
                {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.tenderProductionRate') }} <span class="text-sm text-gray-400">{{ wbsCodeForm.form.unit ? `(${wbsCodeForm.form.unit}${$t('ProjectSettings.ProjectTab.WbsCodeModal.units.perDay')})` : '' }}</span>
              </p>

              <AppInput
                v-model="wbsCodeForm.form.tender_production_rate"
                name="tender_production_rate"
                type="number"
                :error="wbsCodeForm.getErrors('tender_production_rate')"
                data-test="update-tender_production_rate"
                class="form-input"
              />
            </label>
          </div>


          <label
            v-if="!project?.isClient"
          >
            <p>
              {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.tenderQuantity') }} <span class="text-sm text-gray-400">{{ wbsCodeForm.form.unit ? `(${wbsCodeForm.form.unit})` : '' }}</span>
            </p>

            <AppInput
              v-model="wbsCodeForm.form.tender_quantity"
              name="tender_quantity"
              type="number"
              :error="wbsCodeForm.getErrors('tender_quantity')"
              data-test="update-tender_quantity"
              class="form-input"
            />
          </label>

          <label
            v-if="!project?.isClient"
          >
            <p>
              {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.tenderCost') }} <span class="text-sm text-gray-400">($)</span>
            </p>

            <AppInput
              v-model="wbsCodeForm.form.tender_cost"
              name="tender_cost"
              type="number"
              :error="wbsCodeForm.getErrors('tender_cost')"
              data-test="update-tender_cost"
              class="form-input"
            />
          </label>

          <div
            v-if="!project?.isClient"
            class="flex space-x-4"
          >
            <div
              v-if="isNumber(wbsCodeForm.form.tender_quantity / wbsCodeForm.form.tender_cost) && wbsCodeForm.form.unit"
              class="text-center w-1/2 my-auto"
            >
              <p>
                {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.tenderRate') }}
              </p>

              <p>
                <span>
                  {{ `${australianCurrency(wbsCodeForm.form.tender_cost / wbsCodeForm.form.tender_quantity)}/${wbsCodeForm.form.unit}` }}
                </span>
              </p>
            </div>

            <div
              v-if="isNumber(wbsCodeForm.form.tender_quantity / wbsCodeForm.form.tender_cost) && wbsCodeForm.form.unit"
              class="text-center w-1/2 my-auto"
            >
              <p>
                {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.fields.tenderDays') }}
              </p>

              <p>
                <span>
                  {{ `${australianNumber(wbsCodeForm.form.tender_quantity / wbsCodeForm.form.tender_production_rate)} ${$t('ProjectSettings.ProjectTab.WbsCodeModal.units.days')}` }}
                </span>
              </p>
            </div>
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          v-if="$props.wbsCode"
          type="button"
          class="btn btn--tertiary-blue"
          @click="deleteWbsCode"
        >
          {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.buttons.delete') }}
        </button>

        <div class="space-x-2 ml-auto">
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="$emit('close'); wbsCodeForm.resetData()"
          >
            {{ $t('ProjectSettings.ProjectTab.WbsCodeModal.buttons.cancel') }}
          </button>

          <button
            type="button"
            class="btn btn--primary-blue"
            data-test="save-code"
            @click="saveWbsCode"
          >
            {{ $props.wbsCode ? $t('ProjectSettings.ProjectTab.WbsCodeModal.buttons.update') : $t('ProjectSettings.ProjectTab.WbsCodeModal.buttons.create') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import { t } from '@/i18n';
import WbsCode from '@/models/WbsCode';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import Swal from 'sweetalert2';
import { computed, watch } from 'vue';

const props = defineProps<{
  wbsCode?: WbsCode;
  projectId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const wbsCodeStore = useWbsCodesStore();
const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const wbsCodeForm = useFormData({
  code: '',
  description: '',
  unit: '',
  tender_production_rate: 0,
  tender_quantity: 0,
  tender_cost: 0,
});

watch(() => props.wbsCode, () => {
  if(props.wbsCode) {
    wbsCodeForm.setData(props.wbsCode);
  }
}, { immediate: true });

const updateWbsCodeAction = useStoreApiAction(wbsCodeStore.updateCode);
const createWbsCodeAction = useStoreApiAction(wbsCodeStore.createCode);

const saveWbsCode = () => {
  const formData = { ...wbsCodeForm.form };

  if(props.wbsCode) {
    wbsCodeForm.resetErrors();

    updateWbsCodeAction.request(props.wbsCode.id, formData).then(() => {
      Swal.fire({
        icon: 'success',
        text: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.wbsCodeUpdated'),
      }).then(() => {
        emits('close');
        wbsCodeForm.resetData();
      });
    }).catch((error) => {
      wbsCodeForm.setErrors(error.data);
    });
  } else {
    createWbsCodeAction.request(props.projectId, formData).then(() => {
      Swal.fire({
        icon: 'success',
        text: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.wbsCodeCreated'),
      }).then(() => {
        emits('close');
        wbsCodeForm.resetData();
      });
    }).catch((error) => {
      wbsCodeForm.setErrors(error.data);
    });
  }
};

const deleteAction = useStoreApiAction(wbsCodeStore.deleteCode);

const deleteWbsCode = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.deleteConfirmTitle'),
    text: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.deleteConfirmText', { code: props.wbsCode.code }),
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteAction.request(props.wbsCode.id).then(() => {
        Swal.fire({
          icon: 'success',
          text: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.wbsCodeDeleted'),
        }).then(() => {
          emits('close');
        });
      }).catch((error) => {
        if(error?.status === 422) {
          Swal.fire({
            icon: 'error',
            text: t('ProjectSettings.ProjectTab.WbsCodeModal.messages.deleteFailed', { code: props.wbsCode.code }),
          });
        }
      });
    }
  });
};
</script>

<style scoped></style>
